import { extractOrganizationList } from '../utils/organizationList';
import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { organizationAPI, AUTH_LOGIN_EVENT } from '../services/api';
import { isDomRole } from '../utils/roleHelpers';
import { queryKeys } from '../hooks/queries/queryKeys';

// Organization type (matches what's stored in localStorage)
// NOTE (2026-07-05 role-gating sweep): 'super_admin' can never actually be
// this field's value — organization_members.role is constrained to
// 'owner' | 'admin' | 'submissive' | 'member' on the backend
// (VALID_MEMBER_ROLES in authorizationService.ts); 'super_admin' is
// exclusively a PLATFORM role (users.role / useAuth's user.role). Kept in
// this union only so existing comparisons don't fail to compile — see
// roleHelpers.ts for the corrected isDomRole() capability check, and
// docs/reference/bug-registry.md for the full writeup. 'owner' was ADDED
// here because it IS a real value (e.g. solo accounts created via the
// age-verification flow) that this type previously omitted entirely.
export type OrganizationsListState = 'loading' | 'failed' | 'empty' | 'present';

export interface Organization {
  id: string;
  name: string;
  role: 'admin' | 'member' | 'owner' | 'super_admin';
  joinedAt?: string;
  isDefault?: boolean;
  inviteCode?: string;
  memberCount?: number;
  subscriptionTier?: string;
  subscriptionStatus?: string;
  // Spec 243 Phase 2, Amendment D5: is_personal (set once at creation by the
  // "Flying solo" onboarding flow) never reflects membership changes.
  isPersonal?: boolean;
  // Safety-review fix (2026-07-06): is_personal ANDed with a LIVE membership
  // count (computed server-side in getUserOrganizations) — use THIS, not
  // isPersonal, for the widget-mute predicate. Self-corrects once a solo org's
  // creator invites a real partner (is_personal alone never resets).
  isPersonalHierarchy?: boolean;
}

interface OrganizationContextType {
  /** Current selected organization (null if none selected) */
  organization: Organization | null;
  /** All organizations the user belongs to */
  organizations: Organization[];
  /**
   * True while initial load is in progress.
   * Pre-migration vestigial field (Spec 294 Checkpoint 2): the original
   * implementation declared `useState(false)` and never once called its
   * setter with `true` anywhere in the file — no code path ever flipped it,
   * and no consumer in the codebase destructures it (verified by grep). It
   * is always `false`. Preserved as a literal `false` constant rather than
   * wired to the new query's fetch state, matching the pre-migration
   * behavior exactly rather than "fixing" a field nothing reads. Use
   * `organizationsLoaded` for real load-state gating.
   */
  loading: boolean;
  /**
   * True once refreshFromAPI() has resolved (success or failure) at least
   * once. `organization` starts null on every session-boundary crossing
   * (logout clears it) and only repopulates asynchronously — a consumer
   * that treats a transient null as "confirmed no org" (e.g. redirecting
   * away) needs this to distinguish "still loading" from "actually none".
   */
  organizationsLoaded: boolean;
  /** Error message if load failed */
  error: string | null;
  /**
   * 0zzz-uhy: the state of the organization list, derived from the CURRENT query only (never from the stale `error`,
   * which only refreshOrganizations() sets and nothing clears on success, and never from the one-way
   * `organizationsLoaded` latch). ONE source of truth for "may a page say the user has no hierarchy?":
   *  - 'loading': not enabled yet, or the current query has not answered (first load, or re-fetching after a
   *     login/cache reset), or an empty answer is being re-fetched. No claim may be made.
   *  - 'failed': the current query errored (or answered without an `organizations` array) and no non-empty list is
   *     cached. NOTE: this includes a cached EMPTY answer followed by a failed refetch (the error wins over the older
   *     empty answer: the list is unknown again). Filed as a LOW in the registry (0zzz-uhz): that user loses the
   *     "Tell the Helm team" button until a refetch succeeds.
   *  - 'empty': the current query SUCCEEDED with a valid empty array and is not re-fetching. The only state that
   *     proves "no hierarchy".
   *  - 'present': at least one organization is known.
   */
  organizationsListState: OrganizationsListState;
  /** Select a different organization */
  selectOrganization: (org: Organization) => void;
  /**
   * Refresh organization list from API. Resolves with the freshly-fetched
   * list on success so callers can select from confirmed post-refresh data
   * instead of racing stale context state (e.g. auto-switch-on-join).
   * Never rejects — on failure it logs, sets `error`, and resolves `[]`.
   * Callers treat a rejection as "the join/action itself failed", so a
   * refresh failure resolving `[]` (rather than throwing) keeps that
   * distinction intact instead of misrouting into the wrong catch branch.
   */
  refreshOrganizations: () => Promise<Organization[]>;
  /** Get the current organization ID (shortcut) */
  getOrgId: () => string | null;
  /** Get the current user's role in the organization */
  getOrgRole: () => 'admin' | 'member' | 'owner' | 'super_admin' | null;
  /** Check if current user is admin or super_admin */
  isAdmin: () => boolean;
  /** Clear organization selection (logout helper) */
  clearOrganization: () => void;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

const STORAGE_KEY = 'selectedOrganization';
const ORG_CHANGED_EVENT = 'organizationChanged';

function getStoredOrganization(): Organization | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? (JSON.parse(stored) as Organization) : null;
  } catch {
    return null;
  }
}

async function fetchOrganizations(): Promise<Organization[]> {
  const response = await organizationAPI.getAll();
  // 0zzz-uhy: an answer without a real array is a FAILED load, never "no organizations" (see utils/organizationList.ts).
  // The mounted query goes to isError; refreshOrganizations() resolves [] and sets `error`.
  return extractOrganizationList<Organization>(response.data);
}

export const OrganizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();

  // CRITICAL: `organization` (the CURRENT selection) stays genuine
  // synchronous local state, mirroring localStorage directly — NOT derived
  // from the useQuery below. Initialize synchronously to prevent null flash
  // on first render. Components like HelmPage depend on having org data
  // immediately to avoid blank page bugs (see commit da8e630). A useQuery
  // observer starts as `undefined` until its first fetch settles even with
  // `initialData` supplied — the Provider still needs a render cycle to
  // reflect it — so moving `organization` itself onto the query risks
  // reintroducing that exact bug. Only `organizations` (the fetched list)
  // becomes a useQuery; `organization` keeps the pre-migration shape.
  const [organization, setOrganization] = useState<Organization | null>(() => getStoredOrganization());
  const [organizationsLoaded, setOrganizationsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // See the `loading` field's JSDoc above — pre-migration never set this
  // true anywhere; preserved as a dead-but-interface-compatible field.
  const [loading] = useState(false);

  // Gates the organizations query's automatic fetch. Starts true only if a
  // token already exists at mount (mirrors pre-migration's mount-effect
  // token check); flips true on AUTH_LOGIN_EVENT for a fresh login where no
  // token existed yet at mount.
  const [organizationsEnabled, setOrganizationsEnabled] = useState<boolean>(
    () => !!localStorage.getItem('authToken')
  );

  const organizationsQuery = useQuery<Organization[]>({
    queryKey: queryKeys.organizations.contextList(),
    queryFn: fetchOrganizations,
    enabled: organizationsEnabled,
    // Pre-migration refreshFromAPI()/refreshOrganizations() had no retry
    // wrapper — a single attempt per fetch.
    retry: false,
    // One long-lived observer for this query (OrganizationProvider mounts
    // once at App.tsx's root) — every fetch is driven explicitly: initial
    // mount (via `enabled`), the AUTH_LOGIN_EVENT handler below, or
    // refreshOrganizations()'s queryClient.fetchQuery() call. Nothing
    // should implicitly refetch on remount/reconnect/window-focus, matching
    // pre-migration exactly (it only ever reloaded on mount, an explicit
    // refreshOrganizations() call, or AUTH_LOGIN_EVENT — never on focus or
    // reconnect). Same reasoning as FeaturesContext.tsx/PushNotificationContext.tsx.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // lib/queryClient.ts's organizationChanged listener calls
  // queryClient.removeQueries() (no filter — the whole cache) on every
  // GENUINE org-id change (selectOrganization/clearOrganization). That wipes
  // THIS query's cache entry too, and the active observer here rebuilds and
  // refetches (confirmed pattern, see FeaturesContext.tsx's own comment on
  // this exact exposure). During that gap `organizationsQuery.data` is
  // `undefined` — but `organizations` (the full list the user belongs to)
  // is NOT scoped to which org is currently selected, so its true value
  // does not change on a switch at all. Falling back to the last known list
  // during the wipe is therefore not just a stopgap, it's the correct
  // value — and it prevents `organizations` from flashing to `[]` for every
  // consumer that renders off it mid-switch (OrganizationSelector,
  // MobileOrgSwitcher, HierarchySwitcherSheet, MultiHierarchyHint, etc. —
  // several genuine switcher/count UIs, verified via grep). This mirrors
  // Checkpoint 1's hasLoadedOnceRef technique, applied to data instead of a
  // loading flag.
  const lastKnownOrgsRef = useRef<Organization[]>([]);
  useEffect(() => {
    if (organizationsQuery.data) {
      lastKnownOrgsRef.current = organizationsQuery.data;
    }
  }, [organizationsQuery.data]);
  const organizations: Organization[] = organizationsQuery.data ?? lastKnownOrgsRef.current;

  // organizationsLoaded is a one-way latch (set true, never reset false) —
  // mirrors pre-migration's `organizationsLoaded = true` in refreshFromAPI's
  // finally block (ran on success OR failure) and the immediate `true` set
  // when there's no token to load. `organizationsQuery.isFetched` can go
  // back to `false` after the cache-wipe above (removeQueries() deletes the
  // entry entirely) — the latch is what keeps organizationsLoaded from
  // flickering back to false during that rebuild, same guard Checkpoint 1
  // (FeaturesContext.tsx) uses for its own `loading` field via
  // hasLoadedOnceRef.
  useEffect(() => {
    if (!organizationsEnabled || organizationsQuery.isFetched) {
      setOrganizationsLoaded(true);
    }
  }, [organizationsEnabled, organizationsQuery.isFetched]);

  // 0zzz-uhy: the list state, from the CURRENT query (see the interface note). Order matters: a known list wins, then
  // an error, then a settled empty answer; everything else (disabled, pending, re-fetching an empty answer) is loading.
  let organizationsListState: OrganizationsListState;
  if (!organizationsEnabled) organizationsListState = 'loading';
  else if (organizations.length > 0) organizationsListState = 'present';
  else if (organizationsQuery.isError) organizationsListState = 'failed';
  else if (organizationsQuery.isSuccess && !organizationsQuery.isFetching && Array.isArray(organizationsQuery.data)) organizationsListState = 'empty';
  else organizationsListState = 'loading';

  useEffect(() => {
    if (organizationsQuery.isError) {
      // intentional: localStorage fallback remains valid, no user action needed
      console.error('Error fetching organizations:', organizationsQuery.error);
    }
  }, [organizationsQuery.isError, organizationsQuery.error]);

  // Replaces refreshFromAPI()'s 3 post-fetch branches (org-removed,
  // role-synced, auto-selected-single-org). Keyed on `dataUpdatedAt` (not
  // `data`) so it fires exactly once per SUCCESSFUL fetch settlement,
  // including under structural sharing (identical-content refetches keep
  // the same `data` reference but still bump `dataUpdatedAt`) — and does
  // NOT fire on a failed fetch (`dataUpdatedAt` is untouched by errors),
  // matching pre-migration's branches living only inside refreshFromAPI's
  // try block. Re-reads localStorage fresh (via getStoredOrganization())
  // rather than closing over the `organization` state variable — this is
  // deliberate, matching pre-migration's refreshFromAPI exactly (which also
  // re-read localStorage rather than using the `organization` state closure;
  // refreshOrganizations() below is the one function that intentionally
  // reads `organization` state instead — see its own comment).
  //
  // NOTE (Spec 294 Checkpoint 2, documented tradeoff): because `organizations`
  // now lives in a shared query cache instead of isolated component state,
  // this effect also runs after refreshOrganizations()'s queryClient.fetchQuery()
  // call and after a post-org-switch cache rebuild — neither of which ran
  // this role-sync branch pre-migration (pre-migration's refreshOrganizations()
  // had its own narrower 2-branch inline logic with no role-sync at all, and
  // a switch never re-fetched the list at all). In practice this only ever
  // makes the role value MORE consistently fresh, never less — see the
  // "role-sync coupling" note in the implementation report for the full
  // reasoning and why this wasn't worth architecting around.
  useEffect(() => {
    const orgs = organizationsQuery.data;
    if (!orgs) return;

    const stored = getStoredOrganization();
    const foundOrg = stored ? orgs.find((o: Organization) => o.id === stored.id) : null;

    // If current org is no longer in the list, clear selection
    if (stored && !foundOrg) {
      localStorage.removeItem(STORAGE_KEY);
      setOrganization(null);
      window.dispatchEvent(new Event(ORG_CHANGED_EVENT));
    } else if (stored && foundOrg && foundOrg.role !== stored.role) {
      // Role changed on the server — sync localStorage and state so isDominant
      // reflects the live value rather than a stale snapshot from auth setup.
      const updatedOrg = { ...stored, role: foundOrg.role };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedOrg));
      setOrganization(updatedOrg);
      window.dispatchEvent(new Event(ORG_CHANGED_EVENT));
    }

    // If only one org and none selected, auto-select
    if (orgs.length === 1 && !stored) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(orgs[0]));
      setOrganization(orgs[0]);
      window.dispatchEvent(new Event(ORG_CHANGED_EVENT));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationsQuery.dataUpdatedAt]);

  // Listen for organization changes from other components. This is how the
  // Context stays in sync with itself across its own state-mutating methods
  // (selectOrganization/clearOrganization, and the effect above), since they
  // all go through the same "write localStorage, dispatch event" pattern.
  // Some redundancy with the direct setOrganization calls above/below is
  // pre-existing (see loadStoredOrganization's role in the original file) —
  // preserved as-is rather than deduplicated, since it's harmless (re-reading
  // the same value that was just written) and this migration is an internals
  // swap, not a redesign.
  useEffect(() => {
    const handleOrgChanged = () => {
      setOrganization(getStoredOrganization());
    };

    window.addEventListener(ORG_CHANGED_EVENT, handleOrgChanged);
    return () => {
      window.removeEventListener(ORG_CHANGED_EVENT, handleOrgChanged);
    };
  }, []);

  // Listen for login events to fetch organizations after auth. Always
  // (re)triggers a fetch regardless of whether `organizationsEnabled` was
  // already true — mirrors pre-migration's handleLogin, which called
  // refreshFromAPI() unconditionally on every AUTH_LOGIN_EVENT.
  // setOrganizationsEnabled(true) covers the "no token at mount" case (the
  // query has never fetched — enabling it triggers useQuery's normal
  // fetch-on-enable); invalidateQueries covers the "token already existed"
  // case (the query is already enabled and fetched once, so enabling again
  // is a no-op — invalidating an active, fetched observer triggers an
  // immediate background refetch). Together they reproduce "always fetch on
  // login" for both cases without needing to distinguish which one applies.
  useEffect(() => {
    const handleLogin = () => {
      // Reset the fallback ref BEFORE invalidating (skeptical-review finding,
      // 2026-08-04): lastKnownOrgsRef is a plain useRef, outside the
      // TanStack cache queryClient.clear() already wipes on logout
      // (AuthContext.tsx) -- left alone, it silently backfills the PREVIOUS
      // user's organization names/roles into `organizations` for the whole
      // window between this event firing and the new user's fetch
      // resolving, on the common same-tab SPA-navigation logout->relogin
      // path (no page reload). Reproduced via RTL against the real
      // component. Clearing it here closes the exact gap
      // queryClient.clear()'s own contract (see AuthContext.tsx's comment
      // on that call) was meant to guarantee.
      lastKnownOrgsRef.current = [];
      setOrganizationsEnabled(true);
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.contextList() });
    };

    window.addEventListener(AUTH_LOGIN_EVENT, handleLogin);
    return () => {
      window.removeEventListener(AUTH_LOGIN_EVENT, handleLogin);
    };
  }, [queryClient]);

  const selectOrganization = useCallback((org: Organization) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(org));
      setOrganization(org);
      // Dispatch event for other components that might be listening
      window.dispatchEvent(new Event(ORG_CHANGED_EVENT));
    } catch (err) {
      console.error('Error saving organization:', err);
    }
  }, []);

  const clearOrganization = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setOrganization(null);
    window.dispatchEvent(new Event(ORG_CHANGED_EVENT));
  }, []);

  const refreshOrganizations = useCallback(async (): Promise<Organization[]> => {
    try {
      setError(null);
      // fetchQuery (not the plain API call) so this writes through to the
      // SAME cache entry organizationsQuery observes — `organizations`
      // stays in sync with what this call just fetched, matching
      // pre-migration's direct setOrganizations(orgs). retry:false is
      // explicit here because queryClient.fetchQuery() does NOT inherit the
      // mounted useQuery hook's own per-call retry:false override above —
      // that override lives on the observer, not the queryClient's
      // registry — so without this it would silently fall back to the
      // global QueryClient default (retry: 2).
      const orgs = await queryClient.fetchQuery({
        queryKey: queryKeys.organizations.contextList(),
        queryFn: fetchOrganizations,
        retry: false,
        // Explicit staleTime: 0 override (skeptical-review finding,
        // 2026-08-04): without this, fetchQuery() honors the global
        // QueryClient default (staleTime: 30_000, see lib/queryClient.ts)
        // and can silently return a CACHED result without ever calling the
        // API if the last fetch settled under 30s ago -- reproduced against
        // the pinned @tanstack/react-query version. That breaks this
        // function's own documented contract (Spec 287: resolve with the
        // freshly-fetched list so callers like JoinOrganizationPage.tsx/
        // AcceptInvitationPage.tsx/CultivateChat.tsx can find an org they
        // just joined) on the routine path of accepting an invite shortly
        // after login. Matches the existing house pattern in
        // useChastity.ts's own forced-refresh fetchQuery() calls.
        staleTime: 0,
      });

      // Deliberately reads the `organization` state closure (not
      // localStorage) — matches pre-migration's refreshOrganizations()
      // exactly, which used the `organization` state variable here while
      // refreshFromAPI (now the effect above) re-read localStorage. This
      // function's useCallback deps include `organization`, so each call
      // sees the current value.
      // If current org is no longer in the list, clear selection
      if (organization && !orgs.find((o) => o.id === organization.id)) {
        clearOrganization();
      }

      // If only one org and none selected, auto-select
      if (orgs.length === 1 && !organization) {
        selectOrganization(orgs[0]);
      }

      return orgs;
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError('Failed to load organizations');
      // Deliberately resolve (never reject): every existing caller's catch
      // block treats a thrown error as "the join/action itself failed" —
      // rejecting here would misroute a refresh failure into that path.
      // An empty array is also correct on its own terms: callers looking
      // for a just-joined org in the result won't find it in `[]`, which
      // is exactly the "don't switch, don't crash" behavior required.
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, selectOrganization, clearOrganization, queryClient]);

  const getOrgId = useCallback((): string | null => {
    return organization?.id || null;
  }, [organization]);

  const getOrgRole = useCallback((): 'admin' | 'member' | 'owner' | 'super_admin' | null => {
    return organization?.role || null;
  }, [organization]);

  // 2026-07-05: was `role === 'admin' || role === 'super_admin'` — the
  // super_admin branch checked a value organization.role can never hold
  // (see the Organization interface note above); the real, previously-missed
  // gap was 'owner' (a genuinely assigned org role, e.g. solo age-verification
  // accounts). Delegates to the shared isDomRole() helper.
  const isAdmin = useCallback((): boolean => {
    return isDomRole(organization?.role);
  }, [organization]);

  return (
    <OrganizationContext.Provider
      value={{
        organization,
        organizations,
        loading,
        organizationsLoaded,
        error,
        organizationsListState,
        selectOrganization,
        refreshOrganizations,
        getOrgId,
        getOrgRole,
        isAdmin,
        clearOrganization,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

/**
 * Hook to access organization context
 *
 * @example
 * const { organization, isAdmin } = useOrganization();
 *
 * if (!organization) {
 *   return <div>Select an organization</div>;
 * }
 *
 * console.log(`Org: ${organization.name}, Admin: ${isAdmin()}`);
 */
export const useOrganization = (): OrganizationContextType => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

/**
 * Utility hook to get just the organization ID
 * Useful for API calls that need the org ID
 *
 * @example
 * const orgId = useOrganizationId();
 * const { data } = useSWR(orgId ? `/api/org/${orgId}/tasks` : null, fetcher);
 */
export const useOrganizationId = (): string | null => {
  const { organization } = useOrganization();
  return organization?.id || null;
};
